import { useState } from "react";
import usePageLoader from '../hooks/usePageLoader';
import { ShimmerStatCard, ShimmerBox, ShimmerTable } from '../components/common/Shimmer';
import { FiExternalLink, FiUsers, FiMessageCircle, FiActivity, FiRefreshCw } from "react-icons/fi";
import { FaChrome, FaFirefox, FaSafari, FaApple, FaWindows, FaLinux } from "react-icons/fa";
import { useSelector } from "react-redux";

const MOCK_SESSIONS = [
  { id: "V1742746063708833", country: "🇮🇳", ip: "157.20.250.156", browser: "chrome",  os: "mac",     referrer: null,           duration: "00:07:30", messages: 14, conversations: 2, status: "active" },
  { id: "V1742746098321422", country: "🇺🇸", ip: "104.28.12.91",  browser: "chrome",  os: "windows", referrer: "google.com",    duration: "00:03:15", messages: 8,  conversations: 1, status: "active" },
  { id: "V1742746112990041", country: "🇬🇧", ip: "81.2.69.142",   browser: "safari",  os: "mac",     referrer: "twitter.com",   duration: "00:01:44", messages: 4,  conversations: 1, status: "active" },
  { id: "V1742745998001298", country: "🇩🇪", ip: "91.198.174.10", browser: "firefox", os: "linux",   referrer: null,           duration: "00:12:01", messages: 22, conversations: 3, status: "closed" },
  { id: "V1742745812344411", country: "🇦🇺", ip: "203.0.113.55",  browser: "chrome",  os: "windows", referrer: "linkedin.com",  duration: "00:05:10", messages: 11, conversations: 2, status: "closed" },
  { id: "V1742745700981122", country: "🇨🇦", ip: "198.51.100.22", browser: "safari",  os: "mac",     referrer: "nexagpt.ai",    duration: "00:09:43", messages: 19, conversations: 2, status: "closed" },
  { id: "V1742745590244301", country: "🇸🇬", ip: "116.25.88.10",  browser: "chrome",  os: "mac",     referrer: null,           duration: "00:02:55", messages: 6,  conversations: 1, status: "closed" },
  { id: "V1742745480123092", country: "🇯🇵", ip: "150.95.17.34",  browser: "chrome",  os: "windows", referrer: "google.com",    duration: "00:04:22", messages: 9,  conversations: 1, status: "closed" },
];

const BrowserIcon = ({ name }) => {
  if (name === "firefox") return <FaFirefox className="text-orange-500" />;
  if (name === "safari")  return <FaSafari  className="text-blue-500" />;
  return <FaChrome className="text-yellow-500" />;
};

const OsIcon = ({ name }) => {
  if (name === "windows") return <FaWindows className="text-blue-400" />;
  if (name === "linux")   return <FaLinux   className="text-gray-600" />;
  return <FaApple className="text-gray-700" />;
};

function MonitoringSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5">
      <div className="grid grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => <ShimmerStatCard key={i} />)}
      </div>
      <div className="flex gap-1 bg-white border border-gray-200 rounded-xl p-1 w-fit">
        {[...Array(3)].map((_, i) => <ShimmerBox key={i} h="h-8" w="w-20" rounded="rounded-lg" />)}
      </div>
      <ShimmerTable rows={8} cols={9} />
    </div>
  );
}

export default function Monitoring() {
  const [filter, setFilter] = useState("all");
  const { selected } = useSelector((s) => s.chatbots);
  const loading = usePageLoader();

  const active  = MOCK_SESSIONS.filter((s) => s.status === "active");
  const closed  = MOCK_SESSIONS.filter((s) => s.status === "closed");
  const visible = filter === "active" ? active : filter === "closed" ? closed : MOCK_SESSIONS;

  return (
    <div className="-m-6 flex flex-col h-[calc(100vh-4rem)]">

      {/* ── Sticky page header ── */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Monitoring</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Live visitor sessions
            {selected?.chatbot_name && (
              <> · <span className="font-semibold text-gray-700">{selected.chatbot_name}</span></>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 text-green-700 text-xs font-semibold px-3 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            {active.length} live now
          </div>
          <button className="flex items-center gap-1.5 px-3.5 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition">
            <FiRefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {loading ? <MonitoringSkeleton /> : <div className="flex-1 overflow-y-auto bg-gray-50"><div className="p-6 space-y-5">

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <StatCard icon={FiActivity}      label="Active Now"     value={active.length}                                          color="text-green-600" bg="bg-green-50" />
            <StatCard icon={FiUsers}         label="Sessions Today" value={MOCK_SESSIONS.length}                                   color="text-primary"   bg="bg-primary-light" />
            <StatCard icon={FiMessageCircle} label="Messages Today" value={MOCK_SESSIONS.reduce((a, s) => a + s.messages, 0)}      color="text-blue-500"  bg="bg-blue-50" />
          </div>

          {/* Filter tabs */}
          <div className="flex items-center border rounded-lg overflow-hidden text-xs font-medium">
            {[
              { key: "all",    label: "All",    count: MOCK_SESSIONS.length },
              { key: "active", label: "Active", count: active.length },
              { key: "closed", label: "Closed", count: closed.length },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 transition ${
                  filter === t.key ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {t.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  filter === t.key ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
                }`}>
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-gray-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Session</th>
                    <th className="px-4 py-3 text-left font-semibold">Country</th>
                    <th className="px-4 py-3 text-left font-semibold">IP Address</th>
                    <th className="px-4 py-3 text-left font-semibold">Referrer</th>
                    <th className="px-4 py-3 text-left font-semibold">Browser / OS</th>
                    <th className="px-4 py-3 text-left font-semibold">Duration</th>
                    <th className="px-4 py-3 text-center font-semibold">Msgs</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {visible.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50/70 transition">
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">{s.id.slice(0, 18)}…</td>
                      <td className="px-4 py-3 text-base">{s.country}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{s.ip}</td>
                      <td className="px-4 py-3 text-gray-400 italic text-xs">{s.referrer || "direct"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-base">
                          <BrowserIcon name={s.browser} />
                          <OsIcon name={s.os} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-800">{s.duration}</td>
                      <td className="px-4 py-3 text-center text-sm font-semibold text-gray-800">{s.messages}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold ${
                          s.status === "active"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-500"
                        }`}>
                          {s.status === "active" && (
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                          )}
                          {s.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition">
                          <FiExternalLink size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>}

    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = "text-primary", bg = "bg-primary-light" }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-3.5 shadow-sm">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <Icon className={color} size={18} />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
      </div>
    </div>
  );
}
